import type { PostContentJson } from '@_/types';
import type { Node } from '@tiptap/pm/model';
import { createNodeFromContent, getSchema, isNodeEmpty } from '@tiptap/core';
import { Bold } from '@tiptap/extension-bold';
import { BulletList } from '@tiptap/extension-bullet-list';
import { Document } from '@tiptap/extension-document';
import { Italic } from '@tiptap/extension-italic';
import { ListItem } from '@tiptap/extension-list-item';
import { OrderedList } from '@tiptap/extension-ordered-list';
import { Paragraph } from '@tiptap/extension-paragraph';
import { Text } from '@tiptap/extension-text';
import { UndoRedo } from '@tiptap/extensions';
import { DocumentImage } from '../extensions/DocumentImage/index.ts';
import { DocumentTitle } from '../extensions/DocumentTitle/index.ts';
import { Heading } from '../extensions/Heading.ts';
import { BaseContent } from './BaseContent.ts';

const extensions = [
  Document.extend({
    content: `${DocumentTitle.name} block+`
  }),
  Bold,
  BulletList,
  DocumentTitle,
  Heading,
  Italic,
  ListItem,
  DocumentImage,
  OrderedList,
  Paragraph,
  Text,
  UndoRedo
];

const schema = getSchema(extensions);

export class ArticleContent extends BaseContent {
  static extensions = extensions;

  protected get schema() {
    return schema;
  }

  constructor(content: PostContentJson) {
    super(content);
  }

  getTitle(): string | undefined {
    const title = this.doc.firstChild;
    return title?.type.name === DocumentTitle.name ? title.textContent.trim() : undefined;
  }

  static createDefaultContent() {
    return {
      type: 'doc',
      content: [{ type: 'title' }, { type: 'paragraph' }]
    };
  }

  static validate(content: PostContentJson): boolean {
    const nodes: Node[] = [];

    createNodeFromContent(content, schema).forEach(node => {
      nodes.push(node);
    });

    const hasTitle = nodes.some(
      node =>
        node.type.name === DocumentTitle.name && !isNodeEmpty(node, { ignoreWhitespace: true })
    );

    const hasContent = nodes.some(
      node =>
        (node.type.name === Paragraph.name ||
          node.type.name === Heading.name ||
          node.type.name === BulletList.name ||
          node.type.name === OrderedList.name) &&
        !isNodeEmpty(node, { ignoreWhitespace: true })
    );

    return hasTitle && hasContent;
  }
}
